import { useTranslation } from "react-i18next";
import type { ContentLibraryItem } from "@/hooks/useContentLibrary";
import type { RecordingOpts } from "@/components/HomeFeed";
import type { CardContext } from "@/types/cardContext";
import {
  CARD_COLORS,
  bodyStyle,
  cardShellStyle,
  fullCtaStyle,
  labelStyle,
  titleStyle,
} from "./types";

interface GameCardProps {
  game: ContentLibraryItem;
  onRecord: (opts?: RecordingOpts) => void;
  onOpenCollectionPreview?: (templateId: string) => void;
  isCollectionInProgress?: boolean;
}

const GameCard = ({ game, onRecord, onOpenCollectionPreview, isCollectionInProgress }: GameCardProps) => {
  const { t } = useTranslation();
  const colors = CARD_COLORS.game;
  const captureType = game.capture_type ?? "voice";
  const CTA: Record<string, string> = {
    voice:   t("cards.game.ctaVoice"),
    drawing: t("cards.game.ctaDrawing"),
    photo:   t("cards.game.ctaPhoto"),
    written: t("cards.game.ctaWritten"),
    video:   t("cards.game.ctaVideo"),
  };
  const ctaLabel = CTA[captureType] ?? CTA.voice;
  const title = game.title ?? t("cards.game.labelVoice");

  const handleCTA = () => {
    const cardContext: CardContext = {
      contentLibraryId: game.id,
      title,
      body: game.body,
      instructions: game.instructions ?? undefined,
      tip: game.tip ?? undefined,
      captureType,
      tags: game.tags ?? [],
      cardType: game.card_type,
      voiceRequired: game.voice_required,
    };
    onRecord({
      contextPrompt: title,
      captureType: captureType as "voice" | "drawing" | "photo" | "written" | "video",
      cardContext,
      voiceRequired: game.voice_required,
    });
  };

  return (
    <div style={cardShellStyle(colors.bg, colors.border)}>
      {/* Top section — label + title + body */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={labelStyle(colors.body)}>
          {captureType === "drawing" ? t("cards.game.labelDrawing") : captureType === "video" ? t("cards.game.labelVideo") : t("cards.game.labelVoice")}
        </div>

        {game.subject === 'specific_child' && game._resolvedChildName && (
          <div style={{ ...labelStyle(colors.body), marginTop: 4 }}>
            {game._resolvedChildName}
          </div>
        )}

        <p
          style={{
            ...titleStyle(colors.title),
            marginTop: 6,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </p>

        <p
          style={{
            ...bodyStyle(colors.body),
            marginTop: 4,
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {game.body}
        </p>
      </div>

      {/* Collection badge — only when card is a step in a journey */}
      {game._collectionContext && (() => {
        const ctx = game._collectionContext!;
        return (
          <div style={{ paddingTop: 6, borderTop: `1px solid ${colors.border}`, flexShrink: 0 }}>
            <p style={{ fontSize: 11, color: colors.body, opacity: 0.75, margin: 0 }}>
              {t("cards.collection.badge", { collectionName: ctx.templateName })}
            </p>
            <button
              type="button"
              onClick={() => onOpenCollectionPreview?.(ctx.templateId)}
              style={{ background: "none", border: "none", padding: "2px 0 0", fontSize: 11, fontWeight: 600, color: colors.title, cursor: "pointer" }}
            >
              {isCollectionInProgress ? t("cards.collection.continueJourney") : t("cards.collection.startJourney")}
            </button>
          </div>
        );
      })()}

      {/* Bottom: CTA */}
      <div style={{ paddingTop: 8 }}>
        <button
          type="button"
          onClick={handleCTA}
          style={fullCtaStyle(colors.title, colors.bg)}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

export default GameCard;
